/**
 * A modal that asks before doing something that costs money or leaves the
 * building.
 *
 * `window.confirm` would be four lines instead of this file, and it is what the
 * run delete uses. It is not enough here: this action mails a document carrying
 * confidential internal protein ids to an address the user cannot choose, and
 * spends several dollars and ten minutes of a reasoning model doing it. The
 * confirmation has to *name* the target and the recipient, because "are you
 * sure?" over a native dialog is exactly the prompt people dismiss without
 * reading — and the one thing worth checking is the address.
 *
 * Deliberately not a dependency: no dialog library, no focus-trap package. What it
 * does have is the accessibility that makes a hand-rolled modal usable —
 * `role="dialog"`, `aria-modal`, a labelled title, Escape to dismiss, initial focus
 * on the confirm button and focus restored to whatever opened it. Skipping those is
 * what makes hand-rolled modals a mistake; the library is not.
 *
 * It does portal, and that is not a style choice. This dialog is opened from
 * `ReportButton`, which now sits in the `Drawer` footer, and the drawer panel carries
 * `animate-slide-in` — whose fill-mode is `both`, so the transform stays applied and
 * makes the panel the containing block for any `fixed` descendant. Rendered in place,
 * a dialog that says `fixed inset-0` would centre itself inside a 560px drawer with a
 * backdrop covering only the drawer: still operable, silently mis-scoped, and
 * invisible to `tsc`, `eslint` and `test_ui_theme.py` alike. `Drawer` portals for the
 * same reason and records the same fault.
 *
 * The error panel is `FailedState`, which is the same panel four places in this UI
 * used to hand-roll. It is not styling: `FailedState` renders the message verbatim
 * with `role="alert"`, and a failed report request carries the API's own `detail`
 * — the sentence that says whether to retry, reconnect or sign in as someone else.
 */
import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Button } from './ui/Button'
import { FailedState } from './ui/States'

interface Props {
  title: string
  /** The consequences, spelled out. Rendered as-is, so it may hold markup. */
  children: ReactNode
  confirmLabel: string
  /** Set while the action is in flight: both buttons disable, confirm shows it. */
  busy?: boolean
  /**
   * Disable confirm while the body's own input is incomplete — the retire dialog's
   * justification is required, and a disabled button is a clearer answer than a 422.
   *
   * The focus effect below calls `focus()` on the confirm button, which is a no-op on
   * a disabled one, so a body that wants first focus can take it with `autoFocus`
   * without this file knowing about it.
   */
  confirmDisabled?: boolean
  /** Shown above the buttons when the last attempt failed. */
  error?: string | null
  /** A wider panel, for a body that is a list to choose from (the order box). */
  wide?: boolean
  onConfirm: () => void
  onCancel: () => void
}

export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  busy = false,
  confirmDisabled = false,
  error = null,
  wide = false,
  onConfirm,
  onCancel,
}: Props) {
  const confirmRef = useRef<HTMLButtonElement>(null)

  // Focus the confirm button on open and give it back on close. Without the
  // restore, dismissing the dialog drops focus to <body> and a keyboard user
  // has to tab from the top of the page back to the row they were on.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    confirmRef.current?.focus()
    return () => opener?.focus?.()
  }, [])

  // Escape cancels, on the document rather than the dialog: focus may have moved
  // to the backdrop or to a link inside the body text, and a handler bound to
  // the panel would stop working the moment it did.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [busy, onCancel])

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex animate-fade-in items-center justify-center bg-slate-950/70 p-4"
      // A click on the backdrop cancels, but not while the request is in flight:
      // dismissing then would hide a report that has already been started.
      onClick={() => {
        if (!busy) onCancel()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        // Stop the backdrop handler firing for clicks inside the panel —
        // otherwise selecting the recipient text closes the dialog.
        onClick={(e) => e.stopPropagation()}
        className={`w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} animate-rise rounded-xl border border-slate-700 bg-slate-900 p-5 shadow-pop`}
      >
        <h2
          id="confirm-dialog-title"
          className="font-display text-base font-semibold text-slate-100"
        >
          {title}
        </h2>

        <div className="mt-3 space-y-2 text-sm leading-relaxed text-slate-300">
          {children}
        </div>

        {error && <FailedState message={error} className="mt-3" />}

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            ref={confirmRef}
            size="sm"
            onClick={onConfirm}
            disabled={busy || confirmDisabled}
          >
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
